import React, { useState } from "react";
import { Dialog, DialogContent, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useTheme, lighten } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { SendProformaEmployeeCostReportApiService } from "../../../services/send-proforma-employee-cost-report/send-proforma-employee-cost-report-api.service";
import { SentProformaFilter } from "../../../models/sent-proforma-employee-cost-report/sent-proforma-filter";
import { GridColumns } from "@mui/x-data-grid";
import { intlFormatDateShort } from "../../../utils/time-utils";
import Table from "../../../reusable-components/Table/Table";
import DialogTitle from "../../../containers/DialogTitle/DialogTitle";
import AttachedDocuments from "../../../containers/AttachedDocuments/AttachedDocuments";

interface State {
  open: boolean;
}

interface Props {
  proformaEmployeeCostReportId: string;
}

const SentProformaEmployeeCostReport = ({ proformaEmployeeCostReportId }: Props) => {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    open: false,
  });

  const columns: GridColumns = [
    {
      field: "createdAt",
      headerName: "Sent on",
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => <Typography>{intlFormatDateShort(row.createdAt)}</Typography>,
      flex: 0.8,
    },
    {
      field: "createdByUser",
      headerName: "Sent by",
      headerAlign: "center",
      renderCell: ({ row }) => <Typography>{row.createdByUser?.name}</Typography>,
      flex: 1,
    },
    {
      field: "copyToEmails",
      headerName: "Sent To",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Grid>
          {row.copyToEmails.map((address) => (
            <Typography variant="body2">{address}</Typography>
          ))}
        </Grid>
      ),
      flex: 1,
    },
    {
      field: "document",
      headerAlign: "center",
      headerName: "Document",
      align: "center",
      renderCell: ({ row }) => (
        <>
          <AttachedDocuments
            documentIDs={[row.document?.id]}
            count={row.document?.filesCount}
            buttonSize="2x"
            useSimpleView
            documentIsReadOnly
          />
        </>
      ),
      flex: 0.5,
    },
    {
      field: "notification.isDelivered",
      headerAlign: "center",
      headerName: "Is delivered",
      align: "center",
      renderCell: ({ row }) => (
        <>
          {row.notification?.isDelivered ? (
            <Tooltip title={"Delivered"}>
              <span>
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  color={theme.palette.success.main}
                />{" "}
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={row.notification?.error ?? "Not delivered"}>
              <span>
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  color={lighten(theme.palette.error.light, 0.3)}
                />
              </span>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <IconButton onClick={() => setState((state) => ({ ...state, open: true }))}>
        <FontAwesomeIcon
          icon={faCircleCheck}
          color={theme.palette.success.main}
        />
      </IconButton>

      <Dialog
        open={state.open}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          title="Proforma employee cost report sending history"
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />

        <DialogContent>
          {state.open && (
            <Table
              readOnly
              filter=""
              columns={columns}
              translationRootPath=""
              serviceApi={new SendProformaEmployeeCostReportApiService()}
              propsFilter={
                {
                  proformaEmployeeCostReportId: proformaEmployeeCostReportId,
                } as SentProformaFilter
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
export default SentProformaEmployeeCostReport;
